import { z } from "zod";
import { instantSchema } from "./schemas";

/*
 * Runtime schemas of clocking at NFC tags (T-174, ADR-0143). They mirror `backend/openapi.json` like `schemas.ts`.
 * The server never returns the UID or the SUN key of a tag.
 */

/** Kind of a tag: a fixed workplace tag (NTAG 424 DNA) or a personal chip at a terminal. */
export const nfcTagKindSchema = z.enum(["Workplace", "PersonalBadge"]);

/** An NFC tag of an establishment (`GET /nfc-tags`). */
export const nfcTagSchema = z.object({
  id: z.uuid(),
  establishmentId: z.uuid(),
  kind: nfcTagKindSchema,
  name: z.string(),
  employeeId: z.uuid().nullable(),
  createdAt: instantSchema,
  blockedAt: instantSchema.nullable(),
  lastUsedAt: instantSchema.nullable(),
});

/** `GET /nfc-tags`: the tags and whether the plan allows registering new ones. */
export const nfcTagListSchema = z.object({
  entitled: z.boolean(),
  tags: z.array(nfcTagSchema),
});

/** `GET /me/nfc-clocking`: whether the person can clock at a workplace tag. */
export const nfcClockingSchema = z.object({ available: z.boolean() });

/** `POST /events:nfc`: the recorded step. */
export const nfcClockResultSchema = z.object({
  id: z.uuid(),
  status: z.enum(["Accepted", "Duplicate"]),
  occurredAt: instantSchema,
  tagId: z.uuid(),
  tagName: z.string(),
  establishmentId: z.uuid(),
  hints: z.array(z.string()),
});

/** Kind of an NFC tag. */
export type NfcTagKind = z.infer<typeof nfcTagKindSchema>;
/** An NFC tag as HR sees it. */
export type NfcTag = z.infer<typeof nfcTagSchema>;
/** The recorded step at a workplace tag. */
export type NfcClockResult = z.infer<typeof nfcClockResultSchema>;

/** Query keys of the NFC tags. */
export const nfcQueryKeys = {
  mine: ["me", "nfc-clocking"] as const,
  tags: ["nfc-tags"] as const,
};
